/**
 * agreementUtils.js
 *
 * Contiene la función de utilidad para calcular el progreso de los acuerdos.
 */

/**
 * Calcula el progreso de un acuerdo hacia su tope del 200% y determina si está completado.
 * Utiliza la lista de transacciones como la única fuente de verdad para el ROI.
 * @param {object} agreement - El objeto del acuerdo.
 * @param {number} [userCommissions=0] - Las comisiones asignadas a este acuerdo.
 * @param {Array} [allTransactions=[]] - La lista completa de transacciones del usuario.
 * @returns {object} Un objeto con todos los detalles del progreso del acuerdo.
 */
export const getAgreementProgressDetails = (agreement, userCommissions = 0, allTransactions = []) => {
    if (!agreement) {
        return { progress: 0, isComplete: false, roiEarnings: 0, commissionEarnings: 0, totalEarnings: 0, goal: 0, remainingToGoal: 0 };
    }

    // La inversión neta sobre la que se calcula el progreso.
    const investmentAmount = agreement.netInvestmentAmount || agreement.amount || 0;

    // --- NUEVA LÓGICA: El ROI se calcula desde las transacciones, no desde currentValue ---
    const relevantTransactions = allTransactions.filter(tx => {
        if (!tx) return false;
        const relatedId = tx.sourceTransactionId || tx.relatedAgreementId || tx.agreementId;
        if (relatedId !== agreement.id) return false;
        return ['ganancia_roi', 'reversion_ganancia', 'ajuste_manual'].includes(tx.type);
    });

    let roiEarnings = relevantTransactions.reduce((sum, tx) => {
        const amount = Number(tx.amount) || 0;
        if (tx.type === 'ganancia_roi') {
            return sum + amount;
        }
        if (tx.type === 'reversion_ganancia') {
            return sum - Math.abs(amount);
        }
        if (tx.type === 'ajuste_manual' && (!tx.category || tx.category === 'roi')) {
            return sum + amount;
        }
        return sum;
    }, 0);

    // --- NUEVO: Aprovechar totalPaidROI cuando esté disponible ---
    if (!roiEarnings) {
        const totalPaidROI = Number(agreement.totalPaidROI);
        if (Number.isFinite(totalPaidROI) && totalPaidROI > roiEarnings) {
            roiEarnings = totalPaidROI;
        }
    }

    // --- NUEVO: Fallback inteligente usando currentValue ---
    if (!roiEarnings && typeof agreement.currentValue === 'number') {
        const currentValue = Number(agreement.currentValue) || 0;
        const derivedFromDelta = investmentAmount > 0 ? Math.max(0, currentValue - investmentAmount) : 0;
        const derivedFromRaw = Math.max(0, currentValue);
        const bestGuess = Math.max(roiEarnings, derivedFromDelta, derivedFromRaw);
        if (bestGuess > roiEarnings) {
            roiEarnings = bestGuess;
        }
    }
    
    const commissionEarnings = userCommissions || 0;

    // La ganancia total acumulada (ROI de transacciones + comisiones asignadas).
    const totalEarnings = roiEarnings + commissionEarnings;

    // El objetivo es que las ganancias acumuladas (ROI + comisiones) dupliquen la inversión inicial.
    const goal = investmentAmount * 2;

    // Si el acuerdo está completado, los cálculos deben reflejar el estado final alcanzado.
    if (agreement.status === 'completado') {
        const completedRoi = goal - investmentAmount;
        return {
            progress: 100,
            isComplete: true,
            roiEarnings: completedRoi,
            commissionEarnings: commissionEarnings,
            totalEarnings: goal,
            goal: goal,
            remainingToGoal: 0
        };
    }
    
    if (agreement.status !== 'aprobado' || investmentAmount <= 0) {
        return { progress: 0, isComplete: false, roiEarnings: 0, commissionEarnings: 0, totalEarnings: 0, goal: goal, remainingToGoal: goal };
    }

    // El progreso se mide como el porcentaje de las ganancias acumuladas respecto al objetivo de duplicación.
    const progress = goal > 0 ? Math.min((totalEarnings / goal) * 100, 100) : 0;
    
    // El acuerdo está completo cuando las ganancias puras acumuladas alcanzan o superan el objetivo.
    const isComplete = totalEarnings >= goal;
    const remainingToGoal = Math.max(0, goal - totalEarnings);

    return {
        progress,
        isComplete,
        roiEarnings,
        commissionEarnings,
        totalEarnings,
        goal,
        remainingToGoal
    };
};

/**
 * Distribuye el monto total de comisiones de un usuario entre sus acuerdos elegibles.
 * @param {Array<object>} userAgreements - La lista de todos los acuerdos del usuario.
 * @param {number} totalUserCommissions - El monto total de comisiones a distribuir.
 * @param {Array} [allTransactions=[]] - La lista completa de transacciones del usuario.
 * @returns {Map<string, number>} Un mapa donde la clave es el ID del acuerdo y el valor es la comisión asignada.
 */
export const distributeCommissionsToAgreements = (userAgreements, totalUserCommissions, allTransactions = []) => {
    const commissionAllocation = new Map();
    let remainingCommissions = totalUserCommissions;

    if (!userAgreements || userAgreements.length === 0 || remainingCommissions <= 0) {
        return commissionAllocation;
    }

    // 1. Filtrar y ordenar los acuerdos elegibles: solo 'aprobados', con 'acelerador activo' y que NO estén ya completos.
    const eligibleAgreements = userAgreements
        .filter(agreement => 
            agreement.status === 'aprobado' && 
            agreement.acceleratorActive &&
            !getAgreementProgressDetails(agreement, 0, allTransactions).isComplete
        )
        .sort((a, b) => (a.approvedAt?.seconds || a.createdAt.seconds) - (b.approvedAt?.seconds || b.createdAt.seconds));

    // 2. Iterar sobre los acuerdos elegibles y distribuir las comisiones.
    for (const agreement of eligibleAgreements) {
        if (remainingCommissions <= 0) break;

        const { totalEarnings: currentTotalEarnings, goal } = getAgreementProgressDetails(agreement, 0, allTransactions);
        const maxGainFromCommissions = Math.max(0, goal - currentTotalEarnings);
        const commissionForThisAgreement = Math.min(remainingCommissions, maxGainFromCommissions);

        if (commissionForThisAgreement > 0) {
            commissionAllocation.set(agreement.id, commissionForThisAgreement);
            remainingCommissions -= commissionForThisAgreement;
        }
    }

    return commissionAllocation;
};